import DataRow from '@/libs/antd/components/DataRow';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import {
	getPraSurveyDetail,
	SubmitPraSurveySupportingDocumentPayload,
	submitPraSurveySupportingDocument,
} from '@/libs/fleet-api/services/survey';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Radio,
	Row,
	Space,
	Table,
	Tabs,
	Upload,
	UploadFile,
} from 'antd';
import {
	FC,
	Fragment,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FRMPraSurveyInputLegalDocument, {
	useFRMPraSurveyInputLegalDocumentForm,
} from './PraSurveyInputPage.LegalDocument';
import FRMPraSurveyInputBusinessDocumentForm, {
	useFRMPraSurveyInputBusinessDocumentForm,
} from './PraSurveyInputPage.BusinessDocument';
import {
	CustomerBusinessDocument,
	CustomerLegalDocument,
	FileData,
	FilePayload,
	MainInformation,
} from '@/libs/fleet-api/services/types';
import FRMPraSurveyInputReferralDocument, {
	useFRMPraSurveyInputReferralDocumentForm,
} from './PraSurveyInputPage.ReferralDocument';
import FRMPraSurveyInputFinancialReportForm, {
	useFRMPraSurveyInputFinancialReportForm,
} from './PraSurveyInputPage.FinancialReport';
import FRMPraSurveyInputEmploymentContractForm, {
	useFRMPraSurveyInputEmploymentContractForm,
} from './PraSurveyInputPage.EmploymentContract';
import FRMPraSurveyInputBankStatementForm, {
	useFRMPraSurveyInputBankStatementForm,
} from './PraSurveyInputPage.BankStatement';
import { useModals } from '@/components/modals/ModalProvider';
import { DocumentItem } from '@/components/modals/DocumentModal';
import ViewManagementShareholder from '@/libs/antd/components/ViewManagementShareholder';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import dayjs from 'dayjs';
import { FormAlert, useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import { uploader } from '@/libs/fleet-api/uploader';

const useGetPraSurveyDetail = (prospectId: string) => {
	return useQuery({
		queryKey: ['getPraSurveyDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getPraSurveyDetail(id);
		},
		enabled: !!prospectId,
	});
};

type FRMPraSurveyInputContext = null | {
	query: ReturnType<typeof useGetPraSurveyDetail>;
	mainInformation: MainInformation | undefined;
	legalDocumentForm: ReturnType<typeof useFRMPraSurveyInputLegalDocumentForm>;
	businessDocumentForm: ReturnType<
		typeof useFRMPraSurveyInputBusinessDocumentForm
	>;
	referralDocumentForm: ReturnType<
		typeof useFRMPraSurveyInputReferralDocumentForm
	>;
	financialReportForm: ReturnType<
		typeof useFRMPraSurveyInputFinancialReportForm
	>;
	employmentContractForm: ReturnType<
		typeof useFRMPraSurveyInputEmploymentContractForm
	>;
	bankStatementForm: ReturnType<typeof useFRMPraSurveyInputBankStatementForm>;
};

const context = createContext<FRMPraSurveyInputContext>(null);
const { Provider } = context;

export const useFRMPraSurveyInput = () => {
	const values = useContext(context)!;
	const {
		mainInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
		prospectFinancialReports,
		employmentContracts,
		prospectBankStatements,
	} = values.query.data?.data ?? {};

	return {
		...values,
		mainInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
		prospectFinancialReports,
		employmentContracts,
		prospectBankStatements,
	};
};

export type FRMPraSurveyInputPageProps = {
	prospectId: string;
};

type PraSurveyInputForm = {
	bankMandiriSurveyAt: string;
	isSurveyByBankMandiri: boolean;
	bankMandiriSurveyDocument: FilePayload;
};

const FRMPraSurveyInputPage: FC<FRMPraSurveyInputPageProps> = ({
	prospectId = '943P2024110005',
}) => {
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const query = useGetPraSurveyDetail(prospectId);
	const {
		approvalHistory,
		mainInformation,
		customerCompanyManagementInformation,
		customerCompanyShareholderInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		prospectFinancialReports,
		prospectBankStatements,
		employmentContracts,
	} = query.data?.data ?? {};

	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openProspectDetail,
	} = useModals();

	const legalDocumentForm = useFRMPraSurveyInputLegalDocumentForm();
	const businessDocumentForm = useFRMPraSurveyInputBusinessDocumentForm();
	const referralDocumentForm = useFRMPraSurveyInputReferralDocumentForm();
	const financialReportForm = useFRMPraSurveyInputFinancialReportForm();
	const employmentContractForm = useFRMPraSurveyInputEmploymentContractForm();
	const bankStatementForm = useFRMPraSurveyInputBankStatementForm();

	const { form, value } = useForm<PraSurveyInputForm>();

	const [activeTab, setActiveTab] = useState('document');

	const submitSupportingDocumentMutation = useMutation({
		mutationFn: submitPraSurveySupportingDocument,
	});

	const submitSupportingDocument = async () => {
		try {
			const { prospectId } = mainInformation!;
			await form.validateFields();
			await legalDocumentForm.form.validateFields();

			const customerBusinessDocuments =
				businessDocumentForm.form
					.getFieldsValue()
					.items?.filter((doc) => doc.file?.name)
					.map((record) => {
						return {
							id: record.id,
							documentNo: record.documentNo,
							documentDate: record.documentDate,
							file: record.file
								? {
										id: record.file.id,
										name: record.file.name,
										path: record.file.path,
									}
								: null,
							publisherId: record.publisher?.id,
							validityPeriodId: record.validityPeriod?.id,
						} as CustomerBusinessDocument<'payload'>;
					}) ?? [];

			const customerLegalDocuments =
				legalDocumentForm.form
					.getFieldsValue()
					.items?.filter((doc) => doc.file?.name)
					.map((record) => {
						return {
							id: record.id,
							documentNo: record.documentNo,
							documentDate: record.documentDate,
							file: record.file
								? {
										id: record.file.id,
										name: record.file.name,
										path: record.file.path,
									}
								: null,
						} as CustomerLegalDocument<'payload'>;
					}) ?? [];

			const customerReferralDocuments =
				referralDocumentForm.form
					.getFieldsValue()
					.items?.filter((doc) => doc.file?.name)
					.map((record) => {
						const defaultId = Date.now().toString();
						return {
							id: record.id,
							documentNo: record.documentNo,
							documentDate: record.documentDate,
							file: record.file
								? {
										id: record.file?.id ?? defaultId,
										name: record.file?.name ?? 'document',
										path: record.file?.path ?? `customer/${defaultId}`,
									}
								: null,
						} as CustomerLegalDocument<'payload'>;
					}) ?? [];

			const {
				isSurveyByBankMandiri,
				bankMandiriSurveyAt,
				bankMandiriSurveyDocument,
			} = form.getFieldsValue() ?? {};

			await form.validateFields();

			const supportingDocumentPayload: SubmitPraSurveySupportingDocumentPayload = {
				prospectId,
				customerLegalDocuments,
				customerBusinessDocuments,
				customerReferralDocuments,
				bankMandiriSurveyAt,
				isSurveyByBankMandiri,
				bankMandiriSurveyDocument,
			};

			const { data } = await submitSupportingDocumentMutation.mutateAsync(
				supportingDocumentPayload,
			);
			if (data) {
				setActiveTab('financial');
			}
		} catch (err) {
			console.log(err);
		}
	};

	const renderApprovalHistory = () => {
		if (!approvalHistory?.length) return null;
		return (
			<Fragment>
				<FleetTitle>Approval History</FleetTitle>
				<Table
					dataSource={
						approvalHistory?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							width: 1,
							align: 'center',
							dataIndex: 'no',
						},
						{
							title: 'Name',
							dataIndex: 'approvalBy',
						},
						{
							title: 'Date',
							dataIndex: 'approvalDate',
						},
						{
							title: 'Process',
							dataIndex: 'approvalProcess',
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							dataIndex: 'approvalNote',
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
			</Fragment>
		);
	};

	const activeCustomerLegalDocuments = legalDocumentForm.form.isFieldsTouched()
		? legalDocumentForm.form.getFieldsValue().items
		: customerLegalDocuments;

	const activeCustomerBusinessDocuments =
		businessDocumentForm.form.isFieldsTouched()
			? businessDocumentForm.form.getFieldsValue().items
			: customerBusinessDocuments;

	const activeCustomerReferralDocuments =
		referralDocumentForm.form.isFieldsTouched()
			? referralDocumentForm.form.getFieldsValue().items
			: customerReferralDocuments;

	const checkDocumentValid = ({
		documents = [],
		isOptional = false,
	}: {
		documents: CustomerLegalDocument[] | CustomerBusinessDocument[];
		isOptional: boolean;
	}) => {
		return (
			documents.every((record) => {
				return record.isMandatory
					? record.isMandatory && !!record.file?.id
					: record.isMandatory2
						? !!record.file?.id
						: true;
			}) ?? isOptional
		);
	};

	const [validationStatus, setValidationStatus] = useState({
		legal: false,
		business: false,
		referral: false,
		financial: false,
		bank: false,
		contract: false,
	});

	const financialReportItems = financialReportForm.form.isFieldsTouched()
		? financialReportForm.form.getFieldsValue().items
		: prospectFinancialReports;
	const bankStatementItems = bankStatementForm.form.isFieldsTouched()
		? bankStatementForm.form.getFieldsValue().items
		: prospectBankStatements;
	const employmentContractItems = employmentContractForm.form.isFieldsTouched()
		? employmentContractForm.form.getFieldsValue().items
		: employmentContracts;

	useEffect(() => {
		setValidationStatus((current) => ({
			...current,
			legal: checkDocumentValid({
				documents: activeCustomerLegalDocuments as CustomerLegalDocument[],
				isOptional: false,
			}),
			business: checkDocumentValid({
				documents:
					activeCustomerBusinessDocuments as CustomerBusinessDocument[],
				isOptional: false,
			}),
			referral: checkDocumentValid({
				documents: activeCustomerReferralDocuments as CustomerLegalDocument[],
				isOptional: activeCustomerReferralDocuments?.length === 0,
			}),
			financial: (financialReportItems?.length ?? 0) > 0,
			bank: (bankStatementItems?.length ?? 0) > 0,
			contract: (employmentContractItems?.length ?? 0) > 0,
		}));
	}, [
		activeCustomerLegalDocuments,
		activeCustomerBusinessDocuments,
		activeCustomerReferralDocuments,
		financialReportItems,
		bankStatementItems,
		employmentContractItems,
	]);

	if (query.isError) return <GenericErrorMessage />;
	if (query.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Provider
			value={{
				query,
				legalDocumentForm,
				businessDocumentForm,
				referralDocumentForm,
				financialReportForm,
				employmentContractForm,
				bankStatementForm,
				mainInformation,
			}}
		>
			<FleetTitle>Pra Survey Information</FleetTitle>
			<DataRow
				loading={query.isLoading}
				items={[
					[
						[
							'Nama Customer',
							<a
								className="trigger-popup"
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (mainInformation?.customerId)
										openCustomerDetail(mainInformation?.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								className="trigger-popup"
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(prospectId);
								}}
							>
								{prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									className="trigger-popup"
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (mainInformation?.customerGroup)
											openCustomerGroupDetail(
												mainInformation?.customerGroup.id,
											);
									}}
									aria-disabled={!mainInformation?.customerGroup.id}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								className="trigger-popup"
								href="#"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									const legalDocuments = customerLegalDocuments
										?.filter((record) => record.file)
										.map((record) => {
											return {
												documentType: record.documentType,
												file: {
													name: record.file.name,
													url: record.file.url,
												},
											};
										});

									const businessDocuments = customerBusinessDocuments
										?.filter((record) => record.file)
										.map((record) => {
											return {
												documentType: record.documentType,
												file: {
													name: record.file.name,
													url: record.file.url,
												},
											};
										});

									const referralDocuments = customerReferralDocuments
										?.filter((record) => record.file)
										.map((record) => {
											return {
												documentType: record.documentType,
												file: {
													name: record.file.name,
													url: record.file.url,
												},
											};
										});

									if (legalDocuments && legalDocuments.length) {
										legalDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (businessDocuments && businessDocuments.length) {
										businessDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (referralDocuments && referralDocuments.length) {
										referralDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View All Dokumen
							</a>,
						],
					],
				]}
				style={{
					marginBottom: 40,
				}}
			/>

			<Tabs
				activeKey={activeTab}
				onChange={(key) => setActiveTab(key)}
				items={[
					{
						key: 'document',
						label: 'Dokumen Pendukung',
						children: (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								{mainInformation?.customerType?.id === 1 && (
									<Form
										form={form}
										{...ANTD_DEFAULT_PROPS.FORM}
										initialValues={{
											isSurveyByBankMandiri:
												mainInformation?.isSurveyByBankMandiri ?? undefined,
											bankMandiriSurveyAt:
												mainInformation?.bankMandiriSurveyAt ?? undefined,
											bankMandiriSurveyDocument:
												mainInformation?.bankMandiriSurveyDocument ?? undefined,
										}}
										onFinish={submitSupportingDocument}
									>
										<Fragment>
											<Form.Item
												label="Apakah sudah dilakukan survey oleh Bank Mandiri?"
												name="isSurveyByBankMandiri"
												rules={[rule.required()]}
											>
												<Radio.Group
													style={{ display: 'flex', marginLeft: '20px' }}
												>
													<Radio value={true}>Ya</Radio>
													<Radio value={false}>Tidak</Radio>
												</Radio.Group>
											</Form.Item>
											{value?.isSurveyByBankMandiri ? (
												<Fragment>
													<Divider />
													<Row gutter={[40, 20]}>
														<Col span={12}>
															<Form.Item
																label="Tanggal Survey"
																name="bankMandiriSurveyAt"
																rules={[rule.required()]}
																getValueProps={(value) => ({
																	value: value && dayjs(value),
																})}
																labelCol={{
																	span: 8,
																}}
																normalize={(value) =>
																	value && dayjs(value).toISOString()
																}
															>
																<DatePicker />
															</Form.Item>
														</Col>
														<Col span={12}>
															<Form.Item
																name="bankMandiriSurveyDocument"
																label="Dokumen Survey"
																rules={[rule.required()]}
																labelCol={{ span: 8 }}
																style={{ verticalAlign: 'top' }}
																getValueProps={(value) => {
																	const file = value as FileData;
																	return {
																		fileList: value
																			? ([
																					{
																						uid: file.id,
																						name: file.name,
																						thumbUrl: file.url,
																					},
																				] as UploadFile[])
																			: [],
																	};
																}}
																normalize={({ file }: { file: UploadFile }) => {
																	return {
																		id: file.uid,
																		format: file.type,
																		name: file.name,
																		url: file.thumbUrl,
																	} as FileData;
																}}
															>
																<Upload.Dragger
																	listType="picture"
																	accept=".jpg,.jpeg,.png,.pdf"
																	maxCount={1}
																	beforeUpload={() => false}
																	onRemove={() => {
																		form.setFieldValue(
																			'bankMandiriSurveyDocument',
																			null,
																		);
																		return false;
																	}}
																	onChange={async ({ file }) => {
																		const fileid = Date.now().toString();
																		const filepath = `customer/${mainInformation.customerId}/${prospectId}/pra-survey/documents`;

																		try {
																			const url =
																				await uploadMutation.mutateAsync({
																					file: file as unknown as File,
																					fileid,
																					filepath,
																				});
																			form.setFieldValue(
																				'bankMandiriSurveyDocument',
																				{
																					format: 'jpeg',
																					id: fileid,
																					name: file.name,
																					path: filepath,
																					url,
																				},
																			);
																		} catch {
																			form.setFieldValue(
																				'bankMandiriSurveyDocument',
																				null,
																			);
																		}
																	}}
																>
																	<UploadOutlined
																		style={{
																			fontSize: 25,
																			color: '#BABABA',
																			display: 'block',
																		}}
																	/>
																</Upload.Dragger>
															</Form.Item>
														</Col>
													</Row>
													<div style={{ marginBottom: 20 }} />
												</Fragment>
											) : (
												<FormAlert
													alerts={[
														'Jika belum dilakukan survey, maka wajib dilakukan survey',
													]}
												/>
											)}
											{value?.isSurveyByBankMandiri &&
												dayjs(value?.bankMandiriSurveyAt).isBefore(
													dayjs().subtract(6, 'months'),
												) && (
													<FormAlert
														alerts={[
															'Tanggal survey sudah melewati batas waktu. Mohon untuk melakukan survey kembali',
														]}
													/>
												)}
											<div style={{ marginBottom: 20 }} />
										</Fragment>
									</Form>
								)}
								<FRMPraSurveyInputLegalDocument
									valid={validationStatus.legal}
									customerId={mainInformation?.customerId}
								/>
								{customerBusinessDocuments!.length > 0 && (
									<FRMPraSurveyInputBusinessDocumentForm
										valid={validationStatus.business}
										customerId={mainInformation?.customerId}
										sectorIndustry={
											mainInformation?.industrySector.sectorLevel5
										}
										industryClass={
											mainInformation?.industrySector.industryClass
										}
									/>
								)}
								{customerReferralDocuments!.length > 0 && (
									<FRMPraSurveyInputReferralDocument
										valid={validationStatus.referral}
										customerId={mainInformation?.customerId}
									/>
								)}
								<div>
									<FleetTitle>Management / Shareholder Listing</FleetTitle>
									<ViewManagementShareholder
										managements={customerCompanyManagementInformation ?? []}
										shareholders={customerCompanyShareholderInformation ?? []}
									/>
								</div>
								{renderApprovalHistory()}
								<Button
									type="primary"
									style={{
										width: 200,
										display: 'block',
										marginLeft: 'auto',
									}}
									loading={submitSupportingDocumentMutation.isPending}
									onClick={submitSupportingDocument}
								>
									NEXT
								</Button>
							</Space>
						),
					},
					{
						key: 'financial',
						label: 'Financial Information',
						children: (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								<FRMPraSurveyInputFinancialReportForm
									onNext={() => setActiveTab('bank')}
									onPrev={() => setActiveTab('document')}
								>
									{renderApprovalHistory()}
								</FRMPraSurveyInputFinancialReportForm>
							</Space>
						),
					},
					{
						key: 'bank',
						label: 'Data Rekening Bank',
						children: (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								<FRMPraSurveyInputBankStatementForm
									onNext={() => setActiveTab('contract')}
									onPrev={() => setActiveTab('financial')}
								>
									{renderApprovalHistory()}
								</FRMPraSurveyInputBankStatementForm>
							</Space>
						),
					},
					{
						key: 'contract',
						label: 'Data Kontrak Kerja',
						children: (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								<FRMPraSurveyInputEmploymentContractForm
									onPrev={() => setActiveTab('bank')}
									validationStatus={validationStatus}
								>
									{renderApprovalHistory()}
								</FRMPraSurveyInputEmploymentContractForm>
							</Space>
						),
					},
				]}
			/>
		</Provider>
	);
};

export default FRMPraSurveyInputPage;
